import { Box, Typography, Button } from '@material-ui/core'
import { Dispatch, FunctionComponent, SetStateAction } from 'react'
import styled from 'styled-components/macro'
import { UserFeedback } from 'common/types'
import { useNotification } from 'hooks/use-notification/useNotification'
import { useScreenSizes } from 'hooks/use-screen-sizes/useScreenSizes'

const StyledBox = styled(Box)`
  && {
    margin: 1em 0;
    border-style: dashed;
    border-color: ${props => props.theme.colors.accent};
    border-width: thin;
    height: 3em;
    width: 70%;
    border-radius: 15px;
  }
`

const StyledMobile = styled(Box)`
  && {
    border-style: dashed;
    border-color: ${props => props.theme.colors.accent};
    border-width: thin;
    padding: 0.25em;
    height: 2.5em;
    display: block;
    width: 70%;
    border-radius: 15px;
  }
`

interface UploadFeedbackFileProps {
  theme: any
  getProcessedFile: Function
  setFeedback: Function
  feedback: UserFeedback
  fileName: string
  setFileName: Dispatch<SetStateAction<string>>
}

export const UploadFeedbackFile: FunctionComponent<UploadFeedbackFileProps> = ({
  theme,
  getProcessedFile,
  setFeedback,
  feedback,
  fileName,
  setFileName
}) => {
  const { notify } = useNotification()
  const { isDevice } = useScreenSizes()
  const handleUpload = (e: any, fileHandler: 'drag' | 'upload') => {
    const files = fileHandler === 'drag' ? e.dataTransfer.files : e.target.files
    const fileExt = ['pdf', 'doc', 'docx', 'png', 'jpeg', 'jpg']
    const maxImageFileSize = 5
    const fsMb = files[0]?.size / (1024 * 1024)
    if (fsMb > maxImageFileSize) {
      notify('error', 'Upload limit for a file is 5MB')
      e.target.value = ''
      setFileName('')
      return false
    }

    if (!fileExt.includes(files[0]?.name?.toLowerCase()?.split('.')[files[0]?.name?.split('.').length - 1])) {
      notify('error', `Supported file types: PDF, DOC, PNG, JPEG, JPG (5 MB)`)
      e.target.value = ''
      setFileName('')
      return false
    }
    getProcessedFile(files, fileExt, 'stream', async ({ file }: any) => {
      const fileNameArray = files[0]?.name.split('.')
      const fileUrl = JSON.stringify({ file: file, type: fileNameArray[fileNameArray.length - 1] })
      setFeedback({ ...feedback, attachment: fileUrl })
      setFileName(files[0]?.name)
    })
  }

  const handleDelete = e => {
    e.target.value = ''
    setFileName('')
    delete (feedback as any)?.attachment
    setFeedback({ ...feedback, attachment: undefined })
  }

  return (
    <Box justifyContent="space-between" display="flex">
      {isDevice ? (
        <StyledMobile>
          <Typography variant="caption" align="center">
            <Box fontWeight="bold" color={theme.colors.accent}>
              <Box fontSize="9px" mt="0.50em" color={fileName?.length === 0 ? 'inherit' : theme.colors.primary}>
                {fileName?.length === 0 ? 'Supported file types: PDF, DOC, PNG, JPEG, JPG (5 MB)' : fileName}
              </Box>
            </Box>
          </Typography>
        </StyledMobile>
      ) : (
        <StyledBox display="flex" flexDirection="column" alignItems="center">
          <Typography variant="caption" align="center">
            <Box fontWeight="bold" color={theme.colors.accent}>
              <Box mt="0.50em" color={fileName?.length === 0 ? 'inherit' : theme.colors.primary}>
                {fileName?.length === 0 ? 'Supported file types: PDF, DOC, PNG, JPEG, JPG (5 MB)' : fileName}
              </Box>
            </Box>
          </Typography>
        </StyledBox>
      )}

      {!(feedback as any)?.attachment ? (
        <Box display="flex" justifyContent="end" alignItems="center" position={isDevice ? 'relative' : 'initial'}>
          <input
            style={{ display: 'none' }}
            id="raised-button-file"
            type="file"
            onChange={e => handleUpload(e, 'upload')}
          />
          <Button color="primary" variant="contained" component="label" htmlFor="raised-button-file">
            <Typography component="span" variant="body2">
              <Box fontWeight="bold">BROWSE</Box>
            </Typography>
          </Button>
        </Box>
      ) : (
        <Box
          display="flex"
          justifyContent="end"
          alignItems="center"
          position={isDevice ? 'relative' : 'initial'}
          p="0em"
        >
          {(feedback as any)?.attachment?.length > 0 && (
            <Button
              style={{ backgroundColor: theme.colors.error }}
              variant="contained"
              component="label"
              htmlFor="raised-button-file"
              onClick={e => handleDelete(e)}
            >
              <Typography component="span" variant="body2">
                <Box fontWeight="bold">Remove</Box>
              </Typography>
            </Button>
          )}
        </Box>
      )}
    </Box>
  )
}
